import { useEffect, useRef } from 'react';
import { clearAutoHash, markAutoHash } from '../utils/onDemandAutoHash';

/**
 * Keeps the URL hash in sync with the active chapter, so the URL stays shareable as "where you are".
 *
 * Only call this from the page, never from the embedded player: the embed must not touch the host
 * page's URL.
 *
 * The hook only ever removes a hash it wrote itself, tracked per webinar in `writtenFor`. Anything
 * else is someone else's: a genuine deep-link (`#<chapter>` or `#t=<seconds>`) must survive. That
 * matters most on initial load, where there is no active chapter until the timeline and a playback
 * position have arrived — clearing on "no active chapter" alone would wipe the deep-link before the
 * player ever acted on it.
 */
export const useChapterUrlHash = (webinarId: string, activeChapterTitle: string | undefined): void => {
    const writtenFor = useRef<string | null>(null);

    useEffect(() => {
        if (activeChapterTitle) {
            window.history.replaceState(null, '', `#${encodeURIComponent(activeChapterTitle)}`);
            markAutoHash(webinarId, activeChapterTitle);
            writtenFor.current = webinarId;
            return;
        }

        // No chapter is active — the viewer scrubbed back before the first one. Drop our own hash so
        // the URL stops pointing at a chapter they have left.
        if (writtenFor.current !== webinarId) return;

        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        clearAutoHash(webinarId);
        writtenFor.current = null;
    }, [activeChapterTitle, webinarId]);

    // The mark must not outlive the hash it describes. Without this, navigating away (or a
    // same-instance webinarId change) leaves the sessionStorage marker behind; returning to the SAME
    // webinar later in this tab with a hash that happens to coincidentally match it would then be
    // wrongly treated as this hook's own bookkeeping rather than a genuine deep-link. Only touches
    // sessionStorage, not window.location — the URL itself is about to change via navigation anyway.
    useEffect(() => {
        return () => {
            if (writtenFor.current === webinarId) clearAutoHash(webinarId);
        };
    }, [webinarId]);
};
